import React, { useState, useEffect, useMemo, useCallback } from 'react';
import { PageHeader } from '../../PageHeader';
import { Box, BoxForDisplay } from '../../../dtos/box';
import { AjaxResult } from '../../../enums/ajaxResult';
import { Link, Checkbox, FormControlLabel, CircularProgress } from '@material-ui/core';
import { Link as RouterLink } from 'react-router-dom';
import { makeStyles, styled } from '@material-ui/styles';
import { ThemeOptions } from '@material-ui/core/styles/createMuiTheme';
import { OrgSelect } from '../../shared/OrgSelect';
import { useApi } from '../../useApi';
import { useOrgSiteContext } from '../../../hooks/useOrgSiteContext';
import { ValueType } from 'react-select/src/types';
import { DialogSelect } from '../../shared/dialogSelect';
import linq from 'linq';
import { HelpVideo } from '../../../enums/helpVideo';
import { ExportButton } from '../../shared/ExportButton';
import { PageToolbar } from '../../shared/PageToolbar';
import { ResponsiveDataView, Column } from '../../shared/ResponsiveDataView';

import { useAuth } from "../../../features/auth/context";
import { PaginationControls } from "../../PaginationControls";
import { SearchBar } from "../../SearchBar";

export const BoxesPage = () => {
    const classes = useStyles();
    const { boxApi } = useApi();
    const {
        isOrgAdmin,
        isOrgEmployee,
        isSiteAdmin,
        isSiteEmployee,
    } = useAuth();
    const deassocciateSiteFromContext = isOrgAdmin || isOrgEmployee || isSiteAdmin || isSiteEmployee;

    const [boxes, setBoxes] = useState<Box[]>([]);
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [page, setPage] = useState(0);
    const [itemsPerPage, setItemsPerPage] = useState(500);
    const [numberOfPages, setNumberOfPages] = useState(0);
    const [availableOnly, setAvailableOnly] = useState<boolean>(false);
    const [location, setLocation] = useState<string>('');
    const [locations, setLocations] = useState<string[]>([]);
    const [size, setSize] = useState<string>('');
    const [sizes, setSizes] = useState<string[]>([]);
    const [searchText, setSearchText] = useState<string>('');
    const search = useCallback((text: string) => setSearchText(text), []);
    const options = useMemo(
        () => ({
            itemsPerPage,
            page: page + 1,
            availableOnly: availableOnly,
            location: location,
            size: size,
            searchText: searchText,
        }),
        [page, itemsPerPage, availableOnly, location, size, searchText]
    );

    const { dealerId, orgId, siteId, siteChangeCallback } = useOrgSiteContext('boxes');

    useEffect(() => {
        if (boxes !== [] && orgId && siteId) {
            boxApi.getBoxFilterOptions(availableOnly, orgId, siteId).then((r) => {
                if (r.result === AjaxResult.Success && r.data) {
                    setLocations(r.data.locations);
                    setSizes(r.data.sizes);
                }
            });
        }
    }, [boxApi, boxes, availableOnly, orgId, siteId]);

    const locationOptions = useMemo(
        () =>
            linq
                .from(locations)
                .select((l) => ({ value: locations.indexOf(l), label: l }))
                .toArray(),
        [locations]
    );
    const selectedLocationOption =
        linq.from(locationOptions).firstOrDefault((d) => d.label === location) || null;
    const sizeOptions = useMemo(
        () =>
            linq
                .from(sizes)
                .select((l) => ({ value: sizes.indexOf(l), label: l }))
                .toArray(),
        [sizes]
    );
    const selectedSizeOption =
        linq.from(sizeOptions).firstOrDefault((d) => d.label === size) || null;

    useEffect(() => {
        const apiMethod =
            siteId && orgId ? () => boxApi.getBoxesForSite(orgId, siteId, options) : undefined;

        if (apiMethod === undefined) {
            return;
        }

        setIsLoading(true);
        apiMethod().then((r) => {
            setIsLoading(false);
            if (r.result === AjaxResult.Success && r.data) {
                setBoxes(r.data.boxes);
                setNumberOfPages(r.data.numberOfPages);
            }
        });
    }, [orgId, siteId, boxApi, options]);

    const boxesForDisplay = useMemo(
        () =>
            boxes
                .sort((a, b) => (a.number < b.number ? -1 : 1))
                .map(
                    (b) =>
                    ({
                        ...b,
                        status: !b.owners ? 'Open' : '',
                        leaseEndDate:
                            b.leaseEndDate && new Date(b.leaseEndDate).toLocaleDateString(),
                    } as BoxForDisplay)
                ),
        [boxes]
    );

    const reportRequest = useMemo(() => {
        if (!orgId || !siteId) return;
        return () => boxApi.getBoxesReportForSite(orgId, siteId, options);
    }, [orgId, siteId, options, boxApi]);

    const resetFilters = useCallback(() => {
        setSize("");
        setLocation("");
        setAvailableOnly(false);
    }, []);

    const showOrgSelect = !isSiteAdmin && !isSiteEmployee;

    const columns: Column<BoxForDisplay>[] = [
        {
            key: 'number',
            label: 'Box #',
            primary: true,
            render: (b) => (
                <Link
                    component={RouterLink}
                    to={`${dealerId ? `/dealers/${dealerId}` : ''}${orgId ? `/organizations/${orgId}` : ''}${siteId ? `/sites/${siteId}` : ''}/boxDetail/${b.id}`}
                >
                    {b.number}
                </Link>
            ),
        },
        { key: 'availability', label: 'Availability', render: (b) => <AvailabilityIndicator {...b} /> },
        { key: 'location', label: 'Location', hideWhenEmpty: true },
        { key: 'size', label: 'Size', hideWhenEmpty: true },
        { key: 'owners', label: 'Owners', hideWhenEmpty: true },
        { key: 'leaseEndDate', label: 'Lease End Date', hideWhenEmpty: true },
        { key: 'notes', label: 'Notes', hideWhenEmpty: true },
        { key: 'organization', label: 'Organization', hideWhenEmpty: true },
        { key: 'site', label: 'Site', hideWhenEmpty: true },
    ];

    return (
        <>
            <PageHeader helpVideoNum={HelpVideo.Boxes}>Boxes</PageHeader>

            {showOrgSelect && (
                <OrgSelect
                    selectedOrgId={orgId}
                    selectedSiteId={siteId}
                    selectedDealerId={dealerId}
                    bypassSiteContext={deassocciateSiteFromContext}
                    onChange={(val: {
                        dealerId?: number | null;
                        orgId?: number | null;
                        siteId?: number | null;
                    }) => siteChangeCallback(val.dealerId, val.orgId, val.siteId)}
                />
            )}

            <PageToolbar
                search={
                    <SearchBar
                        defaultSearchText={searchText}
                        search={search}
                        labelText="Search Boxes..."
                        onReset={resetFilters}
                    />
                }
                extras={
                    <>
                        <FormControlLabel
                            control={
                                <Checkbox
                                    value={availableOnly}
                                    color={'primary'}
                                    onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                                        setAvailableOnly(event.target.checked)
                                    }
                                    checked={availableOnly}
                                />
                            }
                            label="Available Only"
                        />
                        <DialogSelect
                            options={locationOptions}
                            value={selectedLocationOption}
                            onChange={(value: ValueType<{ value: number; label: string }>) => {
                                if (value === null) setLocation('');
                                else setLocation((value as { value: number; label: string }).label);
                            }}
                            isClearable
                            placeholder={'Select location...'}
                            className={classes.select}
                        />
                        <DialogSelect
                            options={sizeOptions}
                            value={selectedSizeOption}
                            onChange={(value: ValueType<{ value: number; label: string }>) => {
                                if (value === null) setSize('');
                                else setSize((value as { value: number; label: string }).label);
                            }}
                            isClearable
                            placeholder={'Select size...'}
                            className={classes.select}
                        />
                    </>
                }
                actions={
                    <ExportButton
                        apiRequest={reportRequest}
                        targetLabel="Boxes Report"
                        buttonLabel="Export Report"
                        disabled={!orgId || !siteId}
                    />
                }
            />

            <PaginationControls
                page={page}
                setPage={setPage}
                pageCount={numberOfPages}
                itemsPerPage={itemsPerPage}
                setItemsPerPage={setItemsPerPage}
                disabled={isLoading}
            />

            {isLoading ? (
                <CircularProgress />
            ) : (
                <ResponsiveDataView
                    rows={boxesForDisplay}
                    columns={columns}
                    rowKey={(b) => b.id}
                />
            )}
        </>
    );
};

const useStyles = makeStyles(() => ({
    select: {
        minWidth: '200px',
        "@media (max-width: 600px)": {
            minWidth: 0,
            width: '100%',
        },
    },
}));

const AvailabilityIndicator = ({ isExpired, owners }: { owners?: string; isExpired?: boolean }) => {
    let status: 'available' | 'expired' | 'taken';
    if (owners) {
        if (isExpired) {
            status = 'expired';
        } else {
            status = 'taken';
        }
    } else {
        status = 'available';
    }
    switch (status) {
        case 'available':
            return <AvailableIndicatorStyle>Available</AvailableIndicatorStyle>;
        case 'expired':
            return <ExpiredIndicatorStyle>Expired</ExpiredIndicatorStyle>;
        case 'taken':
        default:
            return null;
    }
};

const ExpiredIndicatorStyle = styled('span')(({ theme }: { theme: ThemeOptions }) => ({
    backgroundColor: 'red',
    color: theme.backgroundThemeColor1,
    display: 'inline-block',
    padding: '5px',
    borderRadius: '2.5px',
}));

const AvailableIndicatorStyle = styled('span')(({ theme }: { theme: ThemeOptions }) => ({
    backgroundColor: 'black',
    color: theme.backgroundThemeColor1,
    display: 'inline-block',
    padding: '5px',
    borderRadius: '2.5px',
}));
